import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@/components/data-grid';
import { ROUTES } from '@/app/routes';
import {
  useCoopModerationCases,
  useCoopModerationMetrics,
  type ProactiveCase,
} from '@/features/moderation/api';
import { proactiveColumns } from '@/features/moderation/columns';
import { BulkContentModerationActions } from '../components/BulkContentModerationActions';

/**
 * Content Management → Human Review.
 * Lists Coop cases escalated to TipMi ledger HUMAN_REVIEW (status=escalated).
 * Multi-select Approve / Reject, or open a row for detail.
 */
export function HumanReviewPage() {
  const navigate = useNavigate();
  const table = useCoopModerationCases('escalated');
  const metrics = useCoopModerationMetrics();
  const pending =
    metrics.data?.inHumanQueue ?? table.total ?? 0;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // Drop selection when the visible page / search changes so IDs do not linger off-screen.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [table.page, table.search]);

  const selectedContentIds = useMemo(
    () =>
      [...selectedIds]
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [selectedIds],
  );

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Human Review</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Posts escalated by the moderation pipeline that need a human decision.
          </p>
        </div>
        <span className="rounded-md border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground">
          {pending} pending
        </span>
      </div>

      <div className="min-h-0 flex-1">
        <DataGrid<ProactiveCase>
          title="Human Review Queue"
          searchPlaceholder="Search human review queue…"
          columns={proactiveColumns}
          rows={table.rows}
          total={table.total}
          isLoading={table.isLoading}
          isFetchingNextPage={table.isFetchingNextPage}
          isError={table.isError}
          error={table.error}
          endReached={table.endReached}
          page={table.page}
          totalPage={table.totalPage}
          onPageChange={table.setPage}
          isFetching={table.isFetching}
          onLoadMore={table.fetchNextPage}
          onRetry={() => void table.refetch()}
          search={table.search}
          onSearchChange={table.setSearch}
          onRowClick={(row) => {
            if (row.post_id != null) {
              void navigate(ROUTES.contentDetails(String(row.post_id)));
            }
          }}
          getRowId={(row) => String(row.post_id)}
          rowSelection={{
            selectedIds,
            onChange: setSelectedIds,
          }}
          toolbarActions={
            <BulkContentModerationActions
              contentIds={selectedContentIds}
              onClearSelection={() => setSelectedIds(new Set())}
            />
          }
          emptyMessage="No posts waiting for human review."
        />
      </div>
    </div>
  );
}
